import { Link } from "react-router-dom"
import {
  Target, Eye, Compass, Heart, BookOpen, Users, ShieldCheck, Sparkles,
  GraduationCap, Building2, FlaskConical, Trophy, Library, Microscope,
  CalendarDays, Globe2, HandHeart, Award, ArrowRight, Quote,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { SCHOOL } from "../../lib/constants"

const coreValues = [
  {
    icon: ShieldCheck,
    title: "Integrity",
    text: "We uphold the highest standards of honesty, transparency and ethical conduct in every academic and administrative endeavour. Our community is built on trust, accountability and a shared commitment to doing what is right even when no one is watching.",
    accent: "from-brand-500 to-brand-700",
  },
  {
    icon: Sparkles,
    title: "Excellence",
    text: "We pursue distinction in teaching, research and service. From the lecture hall to the laboratory, we challenge our students and staff to set ambitious goals, embrace continuous improvement and deliver work of lasting value to society.",
    accent: "from-gold-500 to-gold-700",
  },
  {
    icon: HandHeart,
    title: "Service",
    text: "We are stewards of our community and the wider society. Through outreach programmes, teaching practice in rural schools and partnerships with government, we put knowledge to work for the public good and the empowerment of every Nigerian child.",
    accent: "from-brand-600 to-brand-800",
  },
  {
    icon: Globe2,
    title: "Innovation",
    text: "We embrace change, experimentation and the creative application of technology to learning. Our ICT-driven curriculum, blended learning platforms and faculty research projects prepare graduates to thrive in a rapidly evolving world.",
    accent: "from-gold-600 to-gold-800",
  },
]

const leaders = [
  {
    name: "Dr. Aisha Bello-Mahmud",
    role: "Provost",
    bio: "A distinguished scholar of Educational Foundations with over 25 years of experience in teacher education, Dr. Bello-Mahmud leads the College's academic vision and strategic direction.",
    photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=600&auto=format&fit=crop",
  },
  {
    name: "Prof. Ibrahim Sani",
    role: "Deputy Provost (Academic)",
    bio: "Professor of Curriculum Studies overseeing academic programmes, accreditation and faculty development across all five faculties of the College.",
    photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=600&auto=format&fit=crop",
  },
  {
    name: "Dr. Fatima Abdullahi",
    role: "Registrar",
    bio: "Heads the registry department, responsible for admissions, student records, examinations administration and the official correspondence of the College.",
    photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=600&auto=format&fit=crop",
  },
  {
    name: "Bldr. Musa Garba",
    role: "Bursar",
    bio: "Leads the Bursary department with oversight of financial planning, fee administration, payment processing and procurement in line with public sector standards.",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=600&auto=format&fit=crop",
  },
]

const campusLife = [
  {
    icon: Library,
    title: "Modern Library",
    text: "Over 60,000 volumes, e-journals, quiet study areas and a digital resource centre open six days a week.",
  },
  {
    icon: FlaskConical,
    title: "Science Laboratories",
    text: "Fully equipped Chemistry, Physics, Biology and Integrated Science labs supporting NCE practicals and research.",
  },
  {
    icon: Microscope,
    title: "Research Centres",
    text: "Dedicated centres for educational research, language studies and vocational innovation drive faculty output.",
  },
  {
    icon: Trophy,
    title: "Sports & Recreation",
    text: "Football pitch, basketball court, indoor games and annual inter-faculty competitions foster healthy living.",
  },
  {
    icon: Users,
    title: "Student Clubs",
    text: "Debate, drama, journalism, JETS, mathematics and over 20 registered associations enrich campus life.",
  },
  {
    icon: Building2,
    title: "Hostel Accommodation",
    text: "Comfortable on-campus residences for male and female students with 24/7 security and study common rooms.",
  },
]

const timeline = [
  { year: "2014", text: "College founded by the Kano State Government with a mandate to train teachers for upper basic and senior secondary schools." },
  { year: "2016", text: "First cohort of NCE graduates matriculated; NCCE resource verification completed for initial programmes." },
  { year: "2018", text: "Full institutional accreditation achieved; library and science laboratories expanded." },
  { year: "2021", text: "New ICT centre commissioned; e-learning platform introduced for blended teaching." },
  { year: "2024", text: "Tenth anniversary celebrated with over 12,000 alumni and partnerships across five countries." },
]

export default function About() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About" }]}
        subtitle="About the College"
        title="A Decade of Excellence in Teacher Education"
        description={`Founded in ${SCHOOL.established}, ${SCHOOL.fullName} has grown into one of northern Nigeria's most respected colleges of education, blending tradition with innovation.`}
      />

      {/* History */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=1200&auto=format&fit=crop"
                  alt="YMS COE campus"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-brand-800 text-white p-6 rounded-2xl shadow-2xl hidden md:block">
                <CalendarDays className="w-8 h-8 text-gold-300 mb-2" />
                <div className="font-display text-3xl font-bold">{new Date().getFullYear() - SCHOOL.established}+</div>
                <div className="text-xs uppercase tracking-wider opacity-80">Years of Impact</div>
              </div>
            </div>
            <div>
              <Badge variant="success" className="mb-3">Our Story</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                From Humble Beginnings to a Beacon of Learning
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                {SCHOOL.fullName} was established in {SCHOOL.established} by the Kano State Government with a clear mandate: to produce competent, committed and ethical teachers capable of delivering quality education at the basic and senior secondary levels across Nigeria.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                Named after the late Alhaji Yusuf Maitama Sule, the renowned diplomat, orator and statesman, the College draws inspiration from his lifelong advocacy for knowledge, justice and service. From an initial intake of fewer than 200 students, the institution today serves over 12,000 students across 19 academic departments organised into five faculties.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed">
                Our programmes are fully accredited by the National Commission for Colleges of Education (NCCE) and regularly reviewed to align with national curricula, the needs of employers of teachers and the evolving demands of the 21st-century classroom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Milestones</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">A Journey of Growth</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">Key moments that have shaped the College into what it is today.</p>
          </div>
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-brand-200 dark:bg-brand-800 -translate-x-1/2" />
            <div className="space-y-8">
              {timeline.map((t, i) => (
                <div key={t.year} className={`relative flex items-center gap-6 ${i % 2 === 0 ? "md:flex-row-reverse" : ""}`}>
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-gold-500 ring-4 ring-gold-100 dark:ring-gold-900 -translate-x-1/2" />
                  <div className="md:w-1/2 pl-12 md:pl-0 md:px-8">
                    <Card className="border-0 shadow-md">
                      <CardContent className="pt-5">
                        <div className="font-display text-2xl font-bold text-brand-700 dark:text-brand-400 mb-1">{t.year}</div>
                        <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed">{t.text}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Motto */}
      <section className="py-20 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Our Purpose</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold">Mission, Vision & Motto</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-lg bg-gold-500/20 grid place-items-center mb-5">
                <Target className="w-7 h-7 text-gold-300" />
              </div>
              <h3 className="font-display font-bold text-xl mb-3">Our Mission</h3>
              <p className="text-sm text-white/75 leading-relaxed">
                To produce competent, dedicated and innovative teachers through quality instruction, research and community service, grounded in ethical values and responsive to the educational needs of Nigeria and the global community.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-lg bg-brand-500/20 grid place-items-center mb-5">
                <Eye className="w-7 h-7 text-brand-300" />
              </div>
              <h3 className="font-display font-bold text-xl mb-3">Our Vision</h3>
              <p className="text-sm text-white/75 leading-relaxed">
                To be a globally recognised College of Education, renowned for excellence in teacher preparation, educational research and the advancement of pedagogical innovation that transforms communities.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-lg bg-gold-500/20 grid place-items-center mb-5">
                <Compass className="w-7 h-7 text-gold-300" />
              </div>
              <h3 className="font-display font-bold text-xl mb-3">Our Motto</h3>
              <p className="font-display text-lg font-semibold text-gold-300 mb-2">{SCHOOL.motto}</p>
              <p className="text-sm text-white/75 leading-relaxed">
                Knowledge empowers, integrity sustains and service uplifts. These three pillars guide every lesson we teach, every decision we make and every graduate we send into the world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="success" className="mb-3">What We Stand For</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Our Core Values</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              Four guiding principles that shape the character of every YMS COE graduate and inform the culture of our institution.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map(v => (
              <Card key={v.title} className="card-lift border-0 shadow-md overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${v.accent}`} />
                <CardContent className="pt-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${v.accent} grid place-items-center mb-4 shadow-md`}>
                    <v.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">{v.title}</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">{v.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Leadership</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Meet Our Leadership Team</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              Experienced academics and administrators dedicated to advancing the mission of the College.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {leaders.map(p => (
              <Card key={p.name} className="card-lift border-0 shadow-md overflow-hidden group">
                <div className="aspect-[4/5] overflow-hidden bg-ink-100 dark:bg-ink-800">
                  <img src={p.photo} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <CardContent className="pt-5">
                  <Badge variant="success" className="mb-2">{p.role}</Badge>
                  <h3 className="font-display font-bold text-base text-ink-900 dark:text-ink-100 leading-tight">{p.name}</h3>
                  <p className="text-xs text-ink-500 dark:text-ink-400 mt-2 leading-relaxed">{p.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Life */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="success" className="mb-3">Campus Life</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Life at YMS COE</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              Beyond the classroom, our students enjoy a vibrant community rich in learning, friendship and personal growth.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {campusLife.map(c => (
              <Card key={c.title} className="card-lift border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center mb-4">
                    <c.icon className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">{c.title}</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">{c.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-20 bg-ink-50 dark:bg-ink-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Quote className="w-12 h-12 text-gold-400 mx-auto mb-6" />
            <blockquote className="font-display text-2xl md:text-3xl font-medium text-ink-900 dark:text-ink-100 leading-snug mb-6">
              "Knowledge, Integrity and Service — these are not just words on our crest. They are the daily discipline of every member of our community."
            </blockquote>
            <div className="text-sm text-ink-500 dark:text-ink-400">
              <div className="font-semibold text-ink-900 dark:text-ink-100">— College Motto</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-800 to-brand-950 text-white p-10 md:p-16">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-gold-500/10 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Badge variant="warning" className="mb-3">Join Us</Badge>
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Become Part of Our Story</h2>
                <p className="text-white/80 mb-6">
                  Whether you are a prospective student, an aspiring teacher or a partner institution, there is a place for you at Yusuf Maitama Sule College of Education.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/admission-requirements">
                    <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                      View Admission <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/programs">
                    <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">
                      Explore Programmes
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: GraduationCap, value: "12,500+", label: "Students" },
                  { icon: BookOpen, value: "35+", label: "Programmes" },
                  { icon: Users, value: "450+", label: "Staff" },
                  { icon: Award, value: "12K+", label: "Alumni" },
                ].map(s => (
                  <div key={s.label} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5">
                    <s.icon className="w-7 h-7 text-gold-300 mb-2" />
                    <div className="font-display font-bold text-xl">{s.value}</div>
                    <div className="text-xs text-white/70">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
